import { useState } from 'react'
import {
  ContentfulRichText,
  type IOptInType
} from '@pcln/contentful-components'
import useContentfulVariation from '@/hooks/useContentfulVariation'
import {
  OptInCard,
  NOT_SIGNED_UP,
  SIGN_UP_ERROR,
  SIGN_UP_IN_PROGRESS,
  SIGNED_UP,
  EMAIL_TYPE
} from '@pcln/marketing'
import { hasContentTypeId } from '@/shared-utils/content-model-type-guards'
import styled from 'styled-components'
import { Heading, Relative, theme } from 'pcln-design-system'
import CONTENT from './content'
import submitEmail from './utils'
import type { SourceId } from './types'

type EmailSignup = {
  appCode: 'MOBILEWEB' | 'DESKTOP'
  bfcmEmailOptIn?: boolean
  sourceId: SourceId
}

const BFCMEmailWrapper = styled(Relative)`
  input {
    background-color: ${theme.colors.white};
    padding-left: 12px;
  }
`

function mapContentfulContent(optIn: IOptInType) {
  const { url, label } = optIn.callToAction ?? {}
  const { placeholder, error } = optIn?.input?.[0] ?? {}
  const {
    headline = CONTENT.headline,
    subheadline = CONTENT.subheadline,
    successHeadline = CONTENT.successHeadline,
    alertId = CONTENT.alertId,
    channelType = CONTENT.channelType,
    contactSourceType = CONTENT.contactSourceType
  } = optIn

  return {
    headline,
    subheadline,
    successHeadline,
    placeholder: placeholder ?? CONTENT.placeholder,
    invalidEmailError: error ?? CONTENT.invalidEmailError,
    buttonText: label ?? CONTENT.buttonText,
    url: url ?? CONTENT.url,
    alertId,
    channelType,
    contactSourceType
  }
}

function isIOptInType(content: unknown): content is IOptInType {
  return hasContentTypeId(content) && content.contentTypeId === 'optIn'
}

export default function EmailSignUp({
  appCode,
  bfcmEmailOptIn: bcfmEmailOptIn = false,
  sourceId
}: EmailSignup) {
  const optIn = useContentfulVariation('emailOptIn', isIOptInType)
  const hasContentfulData = optIn != null
  const {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError,
    buttonText
  } = hasContentfulData ? mapContentfulContent(optIn) : CONTENT

  const [signUpState, setSignUpState] = useState(NOT_SIGNED_UP)
  const submitHandler = async (data: { email: string }) => {
    try {
      setSignUpState(SIGN_UP_IN_PROGRESS)
      await submitEmail(data.email, sourceId, appCode)
      setSignUpState(SIGNED_UP)
    } catch (err) {
      if (err instanceof Error) {
        setSignUpState(SIGN_UP_ERROR)
      }
    } finally {
      setSignUpState(SIGNED_UP)
    }
  }

  return bcfmEmailOptIn ? (
    <BFCMEmailWrapper
      bg="background.darkest"
      color="white"
      borderRadius="xl"
      paddingTop={20}
      paddingBottom={28}
      paddingX={32}
    >
      <OptInCard
        color="white"
        type={EMAIL_TYPE}
        isNameRequired={false}
        heading={
          <Heading as="h4" paddingTop={2}>
            Sign up for exclusive Cyber Week email-only coupons
          </Heading>
        }
        buttonText={buttonText}
        successMessage={<ContentfulRichText m={0} body={successHeadline} />}
        failureMessage={invalidEmailError}
        handleFormSubmit={submitHandler}
        placeholderText={placeholder}
        signUpState={signUpState}
        longFormat
      />
    </BFCMEmailWrapper>
  ) : (
    <OptInCard
      type={EMAIL_TYPE}
      isNameRequired={false}
      heading={<ContentfulRichText m={0} body={headline} />}
      subheading={<ContentfulRichText m={0} body={subheadline} />}
      buttonText={buttonText}
      successMessage={<ContentfulRichText m={0} body={successHeadline} />}
      failureMessage={invalidEmailError}
      handleFormSubmit={submitHandler}
      placeholderText={placeholder}
      signUpState={signUpState}
      longFormat
    />
  )
}
